import React, { useState, useRef, useEffect } from 'react';
import { useStore } from '../store/useStore';
import { setCurrentUserId, setAuthToken, logout } from '../utils/api';
import styles from './TopBar.module.css';

const ROLE_COLORS = {
  admin:    'var(--accent)',
  pm:       'var(--blue)',
  super:    'var(--green)',
  field:    'var(--text-dim)',
};
const ROLE_LABELS = {
  admin:    'Admin',
  pm:       'Project Manager',
  super:    'Super',
  field:    'Field Staff',
};

export default function TopBar() {
  const {
    view, setView,
    project, setProject,
    projects,
    currentUser, setCurrentUser,
    role,
    isOnline,
    setSignedIn,
  } = useStore();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Project picker — replaces the old "Back to Projects" button. Clicking
  // the project name in the bar opens a dropdown of every project the user
  // has been invited to; picking one switches without a round trip through
  // the projects list. "All projects" at the top still routes back to the
  // grid view for users who want to scan / search the full set.
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);
  const projectPickerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (projectPickerRef.current && !projectPickerRef.current.contains(e.target)) {
        setProjectPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const inProject  = view === 'project';
  const isAdmin    = role === 'admin';

  const handleBackToProjects = () => {
    setProject(null);
    setView('projects');
  };

  const handleAdminConsole = () => {
    setMenuOpen(false);
    setProject(null);
    setView('admin');
  };

  // Real sign-out now: invalidate the JWT client-side, ping the
  // server endpoint (currently a no-op for forward-compat), wipe
  // the user out of the store, and bounce the page to land on
  // SignInScreen.
  const handleSignOut = async () => {
    setMenuOpen(false);
    try { await logout(); } catch (_) {}
    setAuthToken(null);
    setCurrentUserId(null);
    setCurrentUser(null);
    setProject(null);
    setView('projects');
    setSignedIn(false);
  };

  const currentRoleColor = ROLE_COLORS[role] || 'var(--text-dim)';
  const currentRoleLabel = ROLE_LABELS[role] || 'Unknown';

  return (
    <header className={styles.topbar}>
      <div className={styles.logo} onClick={handleBackToProjects} role="button">
        GUMBO<span className={styles.logoAccent}>GRID</span>
      </div>

      {inProject && project && (
        <div className={styles.projectPickerWrap} ref={projectPickerRef}>
          <button
            className={styles.projectPickerBtn}
            onClick={() => setProjectPickerOpen((o) => !o)}
            title="Switch project"
          >
            <span className={styles.projectSlash}>//</span>
            {project.number && <span className={styles.projectNum}>{project.number}</span>}
            <span className={styles.projectTitle}>{project.name}</span>
            <ChevronIcon />
          </button>
          {projectPickerOpen && (
            <div className={styles.projectPickerMenu}>
              <button
                className={styles.projectPickerAll}
                onClick={() => {
                  setProjectPickerOpen(false);
                  handleBackToProjects();
                }}
                title="See all projects in a grid"
              >
                <GridIcon />
                <span>All projects</span>
              </button>
              <div className={styles.projectPickerDivider} />
              {(projects || []).length === 0 ? (
                <div className={styles.projectPickerEmpty}>No other projects</div>
              ) : (
                (projects || []).map((p) => {
                  const isActive = p.id === project.id;
                  return (
                    <button
                      key={p.id}
                      className={`${styles.projectPickerItem} ${isActive ? styles.projectPickerItemActive : ''}`}
                      onClick={() => {
                        if (!isActive) setProject(p);
                        setProjectPickerOpen(false);
                      }}
                    >
                      {p.number && <span className={styles.projectPickerNum}>{p.number}</span>}
                      <span className={styles.projectPickerName}>{p.name}</span>
                      {isActive && <span className={styles.projectPickerCheck}>✓</span>}
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}

      <div className={styles.spacer} />

      {!isOnline && (
        <div className={styles.offlinePill} title="Working offline — changes will sync when reconnected">
          <span className={styles.offlineDot} />
          Offline
        </div>
      )}

      <div className={styles.roleWrap} ref={menuRef}>
        <button
          className={styles.roleBadge}
          style={{ color: currentRoleColor, borderColor: currentRoleColor }}
          onClick={() => setMenuOpen(v => !v)}
        >
          {currentUser ? (
            <>
              <span className={styles.userName}>{currentUser.name}</span>
              <span className={styles.roleDivider}>·</span>
              <span>{currentRoleLabel}</span>
            </>
          ) : (
            <span>No user</span>
          )}
          <ChevronIcon />
        </button>
        {menuOpen && (
          <div className={styles.roleMenu}>
            {currentUser && (
              <div className={styles.roleMenuHeader} style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '10px 12px' }}>
                <span style={{ color: 'var(--text)', fontSize: 12, fontWeight: 600, textTransform: 'none', letterSpacing: 'normal' }}>
                  {currentUser.name}
                </span>
                <span style={{ color: 'var(--text-faint)', fontSize: 11, fontFamily: 'var(--font-mono)' }}>
                  {currentUser.email}
                </span>
                <span style={{ color: currentRoleColor, fontSize: 10, fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 2 }}>
                  {currentRoleLabel}
                </span>
              </div>
            )}
            {isAdmin && (
              <button className={styles.roleMenuItem} onClick={handleAdminConsole}>
                <span className={styles.roleDot} style={{ background: ROLE_COLORS.admin }} />
                <div className={styles.userInfo}>
                  <span className={styles.userInfoName}>Admin Console</span>
                  <span className={styles.userInfoEmail}>Users + future org settings</span>
                </div>
              </button>
            )}
            <button className={styles.signOutBtn} onClick={handleSignOut}>
              SIGN OUT
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

const VersionIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
    <path d="M2 4h12M2 8h8M2 12h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);
const UploadIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
    <path d="M8 1v9M5 4l3-3 3 3M2 12v2a1 1 0 001 1h10a1 1 0 001-1v-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);
const ChevronIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);
const GridIcon = () => (
  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
    <rect x="1.5" y="1.5" width="3.5" height="3.5" rx="0.5" stroke="currentColor" strokeWidth="1.2"/>
    <rect x="7" y="1.5" width="3.5" height="3.5" rx="0.5" stroke="currentColor" strokeWidth="1.2"/>
    <rect x="1.5" y="7" width="3.5" height="3.5" rx="0.5" stroke="currentColor" strokeWidth="1.2"/>
    <rect x="7" y="7" width="3.5" height="3.5" rx="0.5" stroke="currentColor" strokeWidth="1.2"/>
  </svg>
);
